import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import MapErrorBoundary from './MapErrorBoundary';
import MapPropertyPreviewCard from './MapPropertyPreviewCard';
import { getImageUrl, getPropertyImage } from '../utils/imageHelper';
import { formatPrice as formatPriceUtil } from '../utils/currencyPreference';
import 'leaflet/dist/leaflet.css';
import './SearchResultsMap.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const MAP_TILE = {
  url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
  attribution:
    '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>',
};

const parseCoord = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

export const getHotelCoordinates = (hotel) => {
  if (!hotel) return null;
  const pairs = [
    [hotel.location?.lat, hotel.location?.lng],
    [hotel.location?.lat, hotel.location?.lon],
    [hotel.latitude, hotel.longitude],
    [hotel.lat, hotel.lng],
  ];
  for (const [a, b] of pairs) {
    const lat = parseCoord(a);
    const lng = parseCoord(b);
    if (lat != null && lng != null && (lat !== 0 || lng !== 0)) {
      if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        return { lat, lng };
      }
    }
  }
  return null;
};

const pricePinIcon = (priceLabel, isActive) =>
  L.divIcon({
    className: 'srm-pin-root',
    html: `<div class="srm-price-pin ${isActive ? 'srm-price-pin--active' : ''}">${priceLabel}</div>`,
    iconSize: [72, 32],
    iconAnchor: [36, 32],
  });

/** Keeps Leaflet tile layers aligned when the panel is sized via flex/grid. */
const MapResize = ({ trigger }) => {
  const map = useMap();

  useEffect(() => {
    const invalidate = () => {
      map.invalidateSize({ animate: false, pan: false });
    };

    invalidate();
    const raf = window.requestAnimationFrame(invalidate);
    const t1 = window.setTimeout(invalidate, 80);
    const t2 = window.setTimeout(invalidate, 350);

    const wrap = map.getContainer()?.closest('.search-results-map-wrap');
    let observer;
    if (wrap && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(invalidate);
      observer.observe(wrap);
    }

    window.addEventListener('resize', invalidate);
    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      observer?.disconnect();
      window.removeEventListener('resize', invalidate);
    };
  }, [map, trigger]);

  return null;
};

const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    map.invalidateSize({ animate: false, pan: false });
    if (!points.length) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 13);
      return;
    }
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [48, 48], maxZoom: 14 });
  }, [map, points]);
  return null;
};

const PanToSelected = ({ lat, lng, active }) => {
  const map = useMap();
  useEffect(() => {
    if (!active || lat == null || lng == null) return;
    map.panTo([lat, lng], { animate: true, duration: 0.35 });
  }, [map, lat, lng, active]);
  return null;
};

const SearchResultsMap = ({
  hotels = [],
  selectedHotelId,
  onSelectHotel,
  onViewDetails,
  onClearSelection,
  defaultCenter = { lat: 12.9716, lng: 77.5946 },
  searchQuery = '',
  checkIn = null,
  checkOut = null,
}) => {
  const [mapSearch, setMapSearch] = useState('');

  const mappedHotels = useMemo(() => {
    return hotels
      .map((hotel) => {
        const coords = getHotelCoordinates(hotel);
        if (!coords) return null;
        const id = hotel._id || hotel.id;
        const price = hotel.pricePerNight || 0;
        const currency = hotel.currency || 'INR';
        let nights = 1;
        if (checkIn && checkOut) {
          const ms = checkOut.getTime() - checkIn.getTime();
          if (ms > 0) nights = Math.max(1, Math.round(ms / (1000 * 60 * 60 * 24)));
        }
        const total = price * nights;
        const priceLabel =
          total > 0
            ? formatPriceUtil(total, currency).replace(/\s/g, ' ').slice(0, 14)
            : price > 0
              ? formatPriceUtil(price, currency).replace(/\s/g, ' ').slice(0, 14)
              : '—';
        return {
          hotel,
          id,
          ...coords,
          priceLabel,
          image: getPropertyImage(hotel) || getImageUrl(hotel.images?.[0]),
          name: hotel.name || 'Property',
          rating: hotel.rating,
        };
      })
      .filter(Boolean);
  }, [hotels, checkIn, checkOut]);

  const points = mappedHotels.map((m) => ({ lat: m.lat, lng: m.lng }));
  const center = points[0] || defaultCenter;

  const filteredPins = useMemo(() => {
    const q = mapSearch.trim().toLowerCase();
    if (!q) return mappedHotels;
    return mappedHotels.filter((m) => {
      const hay = `${m.name} ${m.hotel.city || ''} ${m.hotel.address || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [mappedHotels, mapSearch]);

  const previewPin = useMemo(() => {
    if (!selectedHotelId) return null;
    return mappedHotels.find((m) => String(m.id) === String(selectedHotelId)) || null;
  }, [mappedHotels, selectedHotelId]);

  const handlePinClick = (pin) => {
    if (selectedHotelId && String(selectedHotelId) === String(pin.id)) {
      onClearSelection?.();
      return;
    }
    onSelectHotel?.(pin.hotel);
  };

  const handleClosePreview = () => {
    onClearSelection?.();
  };

  return (
    <div className="search-results-map-panel">
      <div className="search-results-map-search">
        <input
          type="text"
          value={mapSearch}
          onChange={(e) => setMapSearch(e.target.value)}
          placeholder="Search on map"
          aria-label="Search on map"
        />
      </div>
      <div className="search-results-map-wrap">
        <MapErrorBoundary>
          <MapContainer
            center={[center.lat, center.lng]}
            zoom={12}
            className="search-results-leaflet"
            style={{ width: '100%', height: '100%' }}
            scrollWheelZoom={false}
          >
            <TileLayer url={MAP_TILE.url} attribution={MAP_TILE.attribution} />
            <MapResize trigger={`${points.length}-${filteredPins.length}-${selectedHotelId || ''}`} />
            <FitBounds points={points.length ? points : [defaultCenter]} />
            {previewPin && (
              <PanToSelected
                lat={previewPin.lat}
                lng={previewPin.lng}
                active={!!selectedHotelId}
              />
            )}
            {filteredPins.map((pin) => {
              const isActive = selectedHotelId && String(selectedHotelId) === String(pin.id);
              return (
                <Marker
                  key={pin.id}
                  position={[pin.lat, pin.lng]}
                  icon={pricePinIcon(pin.priceLabel, isActive)}
                  eventHandlers={{
                    click: () => handlePinClick(pin),
                  }}
                />
              );
            })}
          </MapContainer>
        </MapErrorBoundary>

        {previewPin && (
          <div className="srm-preview-anchor">
            <MapPropertyPreviewCard
              hotel={previewPin.hotel}
              checkIn={checkIn}
              checkOut={checkOut}
              onClose={handleClosePreview}
              onViewDetails={onViewDetails}
            />
          </div>
        )}

        {!mappedHotels.length && (
          <div className="search-results-map-empty">
            <p>No map pins for these results.</p>
            {searchQuery && <span>Showing area: {searchQuery}</span>}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchResultsMap;
